import * as Joi from 'joi';
import {
  BankList,
  BusinessBankAccountDepositTypes,
  BusinessBankAccountTypes,
} from '../enums/bank.enums';
import { Gender } from '../enums/gender-enums';
import { Currency } from '../enums/retrieve-transaction.enums';
import { ConvertEnumToStringArray } from '../helpers/enum.helper';

const stringWithNumbersOnlyRegEx = /^\d+$/; // @ref https://github.com/sideway/joi/issues/1157#issuecomment-293538673

export const SettlementSchema = Joi.object()
  .keys({
    accountDepositTypeId: Joi.number()
      .valid(
        BusinessBankAccountDepositTypes.GCASH,
        BusinessBankAccountDepositTypes.PAYMAYA,
        BusinessBankAccountDepositTypes.BANK,
        BusinessBankAccountDepositTypes.NO_ACCOUNT
      )
      .required()
      .messages({
        'any.only': 'Deposit account is invalid.',
        'any.required': 'Deposit account is required.',
        'number.base': 'Deposit account is invalid.',
      }),
    accountTypeId: Joi.number()
      .valid(
        BusinessBankAccountTypes.SAVINGS,
        BusinessBankAccountTypes.CHECKING_CURRENT
      )
      .optional()
      .messages({
        'any.only': 'Account type is invalid.',
        'number.base': 'Account type is invalid.',
      }),
    bankName: Joi.string()
      .valid(...ConvertEnumToStringArray(BankList), 'n/a')
      .messages({
        'any.only': 'Bank name is invalid.',
        'string.base': 'Bank name is invalid.',
        'string.empty': 'Bank name is invalid.',
      }),
    accountName: Joi.string().trim().min(1).max(500).optional().messages({
      'string.base': 'Account name is invalid.',
      'string.empty': 'Account name is invalid.',
      'string.min': 'Account name can at most be 500 characters only.',
      'string.max': 'Account name can at most be 500 characters only.',
    }),
    accountNumber: Joi.string().trim().min(1).max(500).optional().messages({
      'string.base': 'Account number is invalid.',
      'string.empty': 'Account number is invalid.',
      'string.min': 'Account number can at most be 500 characters only.',
      'string.max': 'Account number can at most be 500 characters only.',
    }),
    currencyId: Joi.number()
      .valid(Currency.PHP, Currency.USD)
      .optional()
      .messages({
        'any.only': 'Currency is invalid.',
        'number.base': 'Currency is invalid.',
      }),
  })
  .messages({
    'object.base': 'settlements must contain objects.',
  });

export const BusinessFormSchema = Joi.object({
  aFirstName: Joi.string().trim().min(1).max(100).required().messages({
    'any.required': 'First name is required.',
    'string.base': 'First name is invalid.',
    'string.empty': 'First name is invalid.',
    'string.min': 'First name can at most be 100 characters only.',
    'string.max': 'First name can at most be 100 characters only.',
  }),
  aMiddleName: Joi.string().trim().min(1).max(100).optional().messages({
    'string.base': 'Middle name is invalid.',
    'string.empty': 'Middle name is invalid.',
    'string.min': 'Middle name can at most be 100 characters only.',
    'string.max': 'Middle name can at most be 100 characters only.',
  }),
  aLastName: Joi.string().trim().min(1).max(100).required().messages({
    'any.required': 'Last name is required.',
    'string.base': 'Last name is invalid.',
    'string.empty': 'Last name is invalid.',
    'string.min': 'Last name can at most be 100 characters only.',
    'string.max': 'Last name can at most be 100 characters only.',
  }),
  aPosition: Joi.string().trim().min(1).max(100).required().messages({
    'any.required': 'Position is required.',
    'string.base': 'Position is invalid.',
    'string.empty': 'Position is invalid.',
    'string.min': 'Position can at most be 100 characters only.',
    'string.max': 'Position can at most be 100 characters only.',
  }),
  aEmailAddress: Joi.string().trim().min(1).max(100).required().messages({
    'any.required': 'Email address is required.',
    'string.base': 'Email address is invalid.',
    'string.empty': 'Email address is invalid.',
    'string.min': 'Email address can at most be 100 characters only.',
    'string.max': 'Email address can at most be 100 characters only.',
  }),
  aContactNumber: Joi.string()
    .trim()
    .min(1)
    .max(30)
    .regex(stringWithNumbersOnlyRegEx)
    .required()
    .messages({
      'any.required': 'Contact number is required.',
      'string.base': 'Contact number is invalid.',
      'string.empty': 'Contact number is invalid.',
      'string.pattern.base': 'Contact number is invalid.',
      'string.min': 'Contact number can at most be 30 characters only.',
      'string.max': 'Contact number can at most be 30 characters only.',
    }),
  aDateOfBirth: Joi.date().required().messages({
    'date.base': 'Date is invalid.',
  }),
  aMothersMaidenName: Joi.string().trim().optional().min(0).max(255).messages({
    'string.base': "Mother's maiden name is invalid.",
    'string.empty': "Mother's maiden name is invalid.",
    'string.max': "Mother's maiden name can be at most 255 characters only.",
  }),
  aGender: Joi.string()
    .optional()
    .valid(...Object.keys(Gender), null)
    .messages({
      'string.base': 'Gender is invalid.',
      'string.empty': 'Gender is invalid.',
    }),
  aAubNationalityCode: Joi.string()
    .regex(stringWithNumbersOnlyRegEx)
    .allow('')
    .trim()
    .max(10)
    .min(0)
    .optional()
    .messages({
      'string.base': 'AUB nationality code is invalid.',
      'string.empty': 'AUB nationality code is invalid.',
      'string.pattern.base': 'AUB nationality code is invalid.',
      'string.max': 'AUB nationality code must be at most 10 characters only.',
    }),
  aAubDeviceId: Joi.string().trim().optional().min(0).messages({
    'string.base': 'AUB device ID is invalid.',
    'string.empty': 'AUB device ID is invalid.',
  }),
  aAubIdCode: Joi.string().trim().optional().min(0).max(10).messages({
    'string.base': 'AUB ID code is invalid.',
    'string.empty': 'AUB ID code is invalid.',
    'string.max': 'AUB ID code must have at most 10 characters only.',
  }),
  aAubIdNumber: Joi.string().trim().optional().min(0).max(20).messages({
    'string.base': 'AUB ID number is invalid.',
    'string.empty': 'AUB ID number is invalid.',
    'string.max': 'AUB ID number can be at most 20 characters only.',
  }),
  aAubRegionCode: Joi.string().trim().optional().min(0).max(10).messages({
    'string.base': 'AUB region code is invalid.',
    'string.empty': 'AUB region code is invalid.',
    'string.max': 'AUB region code can be at most 10 characters only.',
  }),

  settlements: Joi.array()
    .items(SettlementSchema)
    .min(1)
    .max(2)
    .required()
    .messages({
      'array.base': 'settlements must be an array.',
      'array.includesRequiredUnknowns': 'settlements has invalid items.',
      'array.min': 'settlements must have at least 1 to 2 items.',
      'array.max': 'settlements must have at least 1 to 2 items.',
    }),
  isWantPackworksWallet: Joi.boolean().required().messages({
    'any.required': 'Field is required.',
    'boolean.base': 'Field is invalid.',
  }),
  isWantPayMayaBankWallet: Joi.boolean().required().messages({
    'any.required': 'Field is required.',
    'boolean.base': 'Field is invalid.',
  }),
  isWantPayMayaBankAccount: Joi.boolean().required().messages({
    'any.required': 'Field is required.',
    'boolean.base': 'Field is invalid.',
  }),

  isAMNameDisabled: Joi.boolean().required().messages({
    'any.required': 'Field is required.',
    'boolean.base': 'Field is invalid.',
  }),

  idsOfValidSignatories: Joi.any(),
  idsOfValidSignatoriesBack: Joi.any(),
  idsOfValidSignatoriesSignature: Joi.any(),

  barangayBusinessPermit: Joi.any(),
  barangayBusinessPermitBack: Joi.any(),
  barangayBusinessPermitSignature: Joi.any(),

  storePhoto: Joi.any(),

  mayorsOrBusinessPermit: Joi.any(),
  mayorsOrBusinessPermitBack: Joi.any(),
  mayorsOrBusinessPermitSignature: Joi.any(),

  birCert2303: Joi.any(),
  birCert2303Back: Joi.any(),
  birCert2303Signature: Joi.any(),

  secCert: Joi.any(),
  secCertBack: Joi.any(),
  secCertSignature: Joi.any(),

  aoiAndByLaws: Joi.any(),
  aoiAndByLawsBack: Joi.any(),
  aoiAndByLawsSignature: Joi.any(),

  gis: Joi.any(),
  gisBack: Joi.any(),
  gisSignature: Joi.any(),

  secretarysCert: Joi.any(),
  secretarysCertBack: Joi.any(),
  secretarysCertSignature: Joi.any(),

  businessTypeId: Joi.any(),
  merchantName: Joi.any(),
  merchantBusinessName: Joi.any(),
  businessHandle: Joi.any(),
  merchantCompanyTin: Joi.any(),

  mainOfficeContactNumber: Joi.any(),
  mainOfficeBldgNameNo: Joi.any(),
  mainOfficeStreetAddress: Joi.any(),
  mainOfficeBarangay: Joi.any(),
  mainOfficeCity: Joi.any(),
  mainOfficeProvince: Joi.any(),
  mainOfficePostalCode: Joi.any(),

  isBrickAndMortarStore: Joi.any(),
  industry: Joi.any(),
  serviceDescription: Joi.any(),
  modeOfPayment: Joi.any(),
  currency: Joi.any(),
  aubPartnerId: Joi.any(),
  estimatedMonthlyGrossTransaction: Joi.any(),

  businessWebsiteUrl: Joi.any(),
  facebookUrl: Joi.any(),
  twitterUrl: Joi.any(),
  instagramUrl: Joi.any(),
  tiktokUrl: Joi.any(),
  isBusinessWebsiteUnderDevelopment: Joi.any(),

  agreeTermsPrivacy: Joi.any(),
  // bldgNameNo: Joi.any(),
});
